/**
 * ============================================================
 *  GOOP ZONE — enemies.ts
 *  Enemy Database & Definitions
 * ============================================================
 *
 *  This module defines all enemy types, their base stats,
 *  sprite assets, and scaling behavior. Each enemy entry is
 *  a static template — runtime instances are created by
 *  enemySystem.ts using these templates.
 *
 *  Follows Megabonk-style enemy design:
 *    - All enemies deal TOUCH damage only (no ranged attacks)
 *    - Enemies walk toward the player and hurt on contact
 *    - Stats scale with time elapsed in the run
 *    - Elite variants have boosted stats + crown indicator
 *
 *  Drop into: client/src/game/enemies.ts
 * ============================================================
 */

// ─── Sprite Direction Type ────────────────────────────────────
// 3-sprite system: up, left, right. No dedicated 'down' sprite.
// When moving directly down, use left or right based on last horizontal direction.
// When moving down-left, use left. When moving down-right, use right.
export type SpriteDirection = 'up' | 'left' | 'right';

// ─── Enemy Rarity / Variant ──────────────────────────────────
export type EnemyVariant = 'normal' | 'elite' | 'boss';

// ─── Enemy Movement Pattern ──────────────────────────────────
export type EnemyMovement =
  | 'chase'       // Walk directly toward player (default)
  | 'chase_fast'  // Same as chase but faster base speed
  | 'intercept'   // BETA 14.28 — Aim at player's predicted future position
                  //  (player.pos + player.velocity * 0.5s) so a kiting player
                  //  gets cut off rather than just trailed. Punishes "run in
                  //  one direction" — best for fast hunter enemies (Timmy).
  | 'swarm'       // Cluster together, slight offset from direct path
  | 'wander'      // Random movement, occasionally charges player
  | 'orbit'       // Circle around player at a distance
  | 'charge'      // Pause, then burst-sprint at player's last position
  | 'flee_low_hp' // Chase normally, flee when below 25% HP
  ;

// ─── Loot Drop Definition ────────────────────────────────────
export interface EnemyLootDrop {
  type: 'xp' | 'goop' | 'chest' | 'health';
  chance: number;       // 0.0 to 1.0 probability
  amount: number;       // base amount (XP value, GOOP coins, HP restored)
}

// ─── Enemy Sprite Set ────────────────────────────────────────
// 3-sprite system: up, left, right. No 'down' sprite needed.
// Downward movement uses left/right based on horizontal component.
export interface EnemySpriteSet {
  up: string;
  left: string;
  right: string;
}

// ─── Eat Attack Definition ───────────────────────────────────
// A heavy enemy grabs a nearby zombie, holds it, then devours it to heal HP.
export interface EatAttackDef {
  grabSprite: string;          // Sprite shown while reaching for the zombie
  holdSprite: string;          // Sprite shown while holding it before eating
  eatSprite: string;           // Sprite shown while chomping
  cooldown: number;            // Seconds between eat attempts (normal)
  bossCooldown: number;        // Seconds between eat attempts (boss)
  grabDuration: number;        // Seconds showing the grab sprite
  holdDuration: number;        // Seconds showing the hold-before-eat sprite
  eatDuration: number;         // Seconds showing the eating sprite
  eatRange: number;            // Max distance (world units) to grab a victim
  healPct: number;             // HP healed as a fraction of enemy's maxHP per eat
  growPct: number;             // spriteSize and hitboxRadius grow by this fraction per eat
  dmgPct?: number;             // BETA 21.17 — damage grows by this fraction per eat (default 0.12)
  speedPct?: number;           // BETA 21.17 — move speed grows by this fraction per eat (default 0.06)
  preferBelowHpPct: number;    // eater prioritises eating when below this HP fraction
  edibleIds: string[];         // Which enemy definitionIds can be eaten
}

// ─── Slam Attack Definition ──────────────────────────────────
export interface SlamAttackDef {
  airborneSprite: string;       // Sprite shown while jumping (airborne ascending frame)
  crackSprite: string;          // Ground crack overlay sprite shown on landing
  cooldown: number;             // Seconds between slams (normal)
  bossCooldown: number;         // Seconds between slams (boss)
  airborneDuration: number;     // Seconds in the air before landing
  landingDuration: number;      // Seconds the crack overlay stays visible
  aoeRadius: number;            // Damage radius on landing (world units)
  damageMult: number;           // Fraction of enemy damage stat applied as AoE on land
  maxRange: number;             // Only slam if player is within this distance
  moveSpeed: number;            // How fast the slammer moves toward slam target while airborne
  // BETA 16.52 — optional 4-frame animation extension for the mega
  // heavies. When set, the airborne phase swaps to `divingSprite` once
  // half the airborneDuration has elapsed (gives a "going up → coming
  // down" read), and the landing phase shows `landingSprite` instead
  // of leaving the airborne sprite up. Regular slammers leave these
  // undefined and use the simpler 1-frame airborne path.
  divingSprite?: string;
  landingSprite?: string;
}

// ─── Throw Attack Definition ─────────────────────────────────
export interface ThrowAttackDef {
  throwSprite: string;                 // Sprite while winding up (arm raised)
  projectileSprite: string;            // Flying projectile sprite
  cooldown: number;                    // Seconds between throws (normal)
  bossCooldown: number;                // Seconds between throws (boss)
  windup: number;                      // Seconds holding pose before releasing
  projectileSpeed: number;             // px/s
  projectileDamageMultiplier: number;  // Fraction of enemy's scaled damage stat
  projectileRadius: number;            // Collision radius of projectile
  projectileRange: number;             // Max travel distance before despawn
  bossScale: number;                   // Projectile size × this for boss variant
  minRange: number;                    // Don't throw if player is closer than this
  maxRange: number;                    // Don't throw if player is farther than this
}

// ─── Enemy Definition (Static Template) ──────────────────────
export interface EnemyDefinition {
  id: string;
  name: string;
  description: string;

  // ── Visual ──
  sprites: EnemySpriteSet;
  eliteSprite: string;          // Single sprite for elite overlay/variant
  spriteSize: number;           // Render size in pixels (before camera zoom)
  hitboxRadius: number;         // Collision circle radius in world units

  // ── Base Stats (at time 0:00) ──
  baseHP: number;
  baseDamage: number;           // Touch damage per hit
  baseSpeed: number;            // Pixels per second
  knockbackResist: number;      // 0.0 (full knockback) to 1.0 (immune)
  contactKnockback: number;     // BETA 21.70/21.77 — base shove force this enemy applies to the PLAYER on a contact hit. Final = contactKnockback + 3.5×scaled-damage, capped at contactKnockback×4 (so raising it in the dashboard flings you further). Per-enemy so heavies shove harder than a zombie.
  contactCooldown: number;      // Seconds between damage ticks when touching player
  iFrameGrant: number;          // Seconds of i-frames granted to player on hit

  // ── Movement ──
  movement: EnemyMovement;

  // ── Scaling Per Minute ──
  scaling: {
    hpPerMinute: number;        // Multiplier added per minute (e.g., 0.12 = +12%/min)
    damagePerMinute: number;
    speedPerMinute: number;
  };

  // ── Elite Variant Multipliers ──
  eliteMultipliers: {
    hp: number;
    damage: number;
    speed: number;
    size: number;               // Sprite scale multiplier
  };

  // ── Boss Variant Multipliers (optional — only for boss-able enemies) ──
  bossMultipliers?: {
    hp: number;
    damage: number;
    speed: number;
    size: number;
    shieldPct: number;       // 0.0 = no shield, 0.5 = shield = 50% of final HP
    knockbackResist?: number; // Override knockback resist for boss form (0–1). Defaults to base + 0.12
  };

  // ── Loot Drops ──
  drops: EnemyLootDrop[];
  eliteDrops: EnemyLootDrop[];  // Additional drops for elite variant

  // ── Spawn Rules ──
  spawnWeight: number;          // Relative weight in spawn pool (higher = more common)
  minSpawnTime: number;         // Seconds into run before this enemy can appear
  maxActiveCount: number;       // Max simultaneous instances (0 = unlimited)

  // ── Tags for future systems ──
  tags: string[];

  // ── Rendering flags ──
  flipHorizontal?: boolean;  // Mirror sprite when facing right (for single-sprite enemies)

  // ── Throw Attack (optional — ranged projectile ability) ──
  throwAttack?: ThrowAttackDef;

  // ── Slam Attack (optional — close-range jump slam) ──
  slamAttack?: SlamAttackDef;

  // ── Eat Attack (optional — grab a nearby enemy, devour it, heal HP) ──
  eatAttack?: EatAttackDef;
}

// ─── Runtime Enemy Instance ──────────────────────────────────
// Created by enemySystem.ts from an EnemyDefinition template
export interface EnemyInstance {
  id: number;                   // Unique runtime ID
  definitionId: string;         // References EnemyDefinition.id
  variant: EnemyVariant;

  // ── Position & Movement ──
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  facing: SpriteDirection;

  // ── Runtime Stats (after scaling) ──
  currentHP: number;
  maxHP: number;
  damage: number;
  speed: number;
  spriteSize: number;
  hitboxRadius: number;
  /** BETA 15.46 — Separate pathing radius (movement-only collision check).
   *  Capped at PATHING_RADIUS_CAP=50 so big enemies can fit through
   *  136wu-wide E/W doorways. Bullet hits
   *  + touch-damage-to-player still use the FULL hitboxRadius so bosses feel
   *  huge and threatening. Per Dylan: "yes for all enemies." */
  pathingRadius: number;
  shieldHP: number;        // Current shield (absorbs damage before HP)
  maxShieldHP: number;     // Max shield (0 = no shield)

  // ── State ──
  alive: boolean;
  /** BETA 14.22 — Per-enemy contact cooldown remaining, in seconds. Decremented
   *  by dt each frame in the enemy update loop. When it hits 0, the enemy is
   *  eligible to deal contact damage to the player; on a successful hit, this
   *  is reset to def.contactCooldown. Replaces the previous lastDamageTime
   *  approach which used absolute state.time and broke when the lab time-lock
   *  froze state.time (one hit per enemy then permanent silence in a swarm). */
  contactCooldownRemaining: number;
  /** BETA 14.29 — Seconds this enemy has been continuously trailing the
   *  moving player (behind player + player moving). Resets to 0 when not
   *  trailing. When >= 3s, the periodic retreat-trigger may flag this
   *  enemy for a peel-off (sets retreatUntil so it visibly runs away). */
  trailingSince: number;
  /** BETA 14.29 — Seconds remaining of "retreat" mode. While > 0, the
   *  enemy moves AWAY from the player at full speed instead of toward.
   *  After it expires, the enemy is typically off-screen and the next
   *  Flank Warp tick will recycle it to the forward arc. User: "if there
   *  are 20 behind me for more than 3 seconds half of them should run
   *  off screen." */
  retreatUntil: number;
  spawnTime: number;            // When this enemy was spawned (run time in seconds)
  flashTimer: number;           // Hit flash effect timer
  knockbackX: number;           // Current knockback velocity
  knockbackY: number;

  // ── Corner-escape ("wall climb") ──
  stuckTimer: number;           // Accumulates while enemy isn't moving; triggers phase when > 0.25s
  phaseTimer: number;           // When > 0, wall collision is ignored ("climbing over") + sprite-grow visual plays
  phaseMaxTimer: number;        // Initial value when phase started — used to compute climb scale
  /** BETA 15.52 — Wall-hop progress tracker. Accumulates when the enemy ISN'T
   *  closing distance to player (whether due to sliding along a wall, blocked
   *  movement, or any other "no-progress" cause). When >= 2.0s, an instant
   *  teleport jump fires in the direction of the player + the climb-scale
   *  visual plays for 0.4s. Per Dylan: "if a zombie is running at a wall
   *  for like 2 seconds... they can jump the wall with one jump." Cleaner
   *  than the BETA 15.50 fullyBlocked check which never fired for the very
   *  common slide-along-wall case. */
  wallPressTimer: number;
  /** BETA 15.52 — distance to player at last sample, for progress tracking. */
  lastDistToPlayer: number;
  /** BETA 15.63 — Jump arc lerp endpoints. Set when wall-hop fires; the
   *  enemy lerps from (jumpStartX, jumpStartY) → (jumpTargetX, jumpTargetY)
   *  over the phaseTimer duration, instead of teleporting instantly.
   *  Per Dylan: "are wea ble to make it so we can see the zombie do the
   *  full jump? instead of teleport?" */
  jumpStartX: number;
  jumpStartY: number;
  jumpTargetX: number;
  jumpTargetY: number;

  // ── Room-aware Navigation ──
  navWaypointX: number;         // X of current nav waypoint; 0/0 = go directly to player
  navWaypointY: number;         // Y of current nav waypoint
  navUpdateTimer: number;       // Countdown (s) until waypoint is recalculated
  navPath: Array<{ x: number; y: number }>;  // A* path waypoints (open-ground navigation)
  navHoldRoom: boolean;         // True when player is outside all rooms — enemy stays put

  // ── Final Boss Flag (BETA 13.37) ──
  /** When true, this enemy ignores all wall/room/exterior-green collision —
   *  glides through everything to reach the player. Set by spawnFinalBoss(). */
  isFinalBoss?: boolean;

  // ── Sprite ──
  spriteLoaded: boolean;
  currentSprite: string;        // Current sprite URL based on facing direction

  // ── Throw Attack Runtime State ──
  throwTimer: number;           // Counts down to 0 → triggers windup
  throwWindupTimer: number;     // Counts down during windup; 0 = not winding up
  throwTargetX: number;         // Player position captured at windup start
  throwTargetY: number;

  // ── Slam Attack Runtime State ──
  slamTimer: number;            // Countdown to next slam attempt
  slamPhase: 0 | 1 | 2;        // 0 = idle, 1 = airborne, 2 = landing (crack)
  slamPhaseTimer: number;       // Time remaining in current slam phase
  slamTargetX: number;          // Position captured when slam started
  slamTargetY: number;

  // ── Eat Attack Runtime State ──
  eatTimer: number;             // Countdown to next eat attempt
  eatPhase: 0 | 1 | 2 | 3;     // 0 = idle, 1 = grab, 2 = hold-before-eat, 3 = eating
  eatPhaseTimer: number;        // Time remaining in current eat phase
  eatTargetId: number;          // Runtime ID of the zombie being eaten (-1 = none)

  // ── Pack Lunge Runtime State (BETA 13.23 — base zombies only, after minute 5) ──
  lungeCooldown: number;        // Seconds until next lunge attempt eligible
  lungeUntil: number;           // While > 0, enemy is mid-lunge (speed × 1.5)

  // ── Status Effects (BETA 13.95 — applied by new weapons) ──
  /** When > 0, enemy is locked in place (speed=0). Works on bosses too.
   *  Set by Ugly Kittens (3s) and Frozen Darts (2s). */
  frozenUntil: number;
  /** When > 0, enemy moves at speed × slowMultiplier. */
  slowedUntil: number;
  /** Multiplier while slowedUntil > 0 (e.g. 0.5 = half speed). */
  slowMultiplier: number;
  /** When > 0, enemy has WET status (Phase 3: 1.5× damage from electric weapons). */
  wetUntil: number;
  // ── BETA 14.04 — Burn DoT (Big Mac Special Sauce item) ──
  /** Time in seconds until burn expires. Set by on-hit roll: 25% chance × stacks
   *  → state.time + 4 + (stacks-1)*1.5. Tick burnDps damage every 0.5s while > now. */
  burnUntil: number;
  /** Damage per second applied while burnUntil > now (3 dmg/s flat — doesn't stack). */
  burnDps: number;
  /** Accumulator so burn applies whole damage on each 0.5s tick. */
  burnTickAccum: number;
  /** BETA 22.74 — what set the current burn, for telemetry attribution.
   *  'item_*' ids route to item damage tracking; other sources (weapon burn,
   *  e.g. 'weapon:fireball_wand') skip item attribution. Cleared when the
   *  burn expires. Absent = legacy Big Mac attribution. */
  burnSource?: string;
  /** BETA 23.6x — CO-OP PHASE 2 part C (§3): roster PlayerId ('p1'..'p4') of the
   *  player whose hit applied the current burn DoT. Attribution CARRY only —
   *  nothing consumes it yet; Bram's E2 unified death pipeline / E4+ ledger reads
   *  it (burn kills currently drop no loot + skip kill credit — his fix credits
   *  this player). Cleared with the burn alongside burnSource. */
  burnAppliedBy?: string;
  /** BETA 23.6x — CO-OP PHASE 2 part C (§3): roster PlayerId whose thorns stat
   *  last reflected damage into this enemy (re-stamped on every reflect hit).
   *  Same carry-only contract + E2/E4+ consumer as burnAppliedBy. */
  thornsAppliedBy?: string;
}


// ═══════════════════════════════════════════════════════════════
//  ENEMY DATABASE
// ═══════════════════════════════════════════════════════════════

export const ENEMY_DATABASE: Record<string, EnemyDefinition> = {

  // ─────────────────────────────────────────────────────────────
  //  ZOMBIE — The Basic Grunt
  // ─────────────────────────────────────────────────────────────
  //  The bread-and-butter zombie. Slow, low HP, comes in hordes.
  //  Holds a cell phone displaying "VIEWBOTTING".
  // ─────────────────────────────────────────────────────────────
  zombie: {
    id: 'zombie',
    name: 'Zombie',
    description: 'A mindless zombie glued to its phone, shambling toward you.',

    sprites: {
      up:    '/cdn/viewbotter_walk_up_14947ec8.png',
      left:  '/cdn/viewbotter_walk_left_e93c6345.png',
      right: '/cdn/viewbotter_walk_right_c1f7adc7.png',
    },
    eliteSprite: '/cdn/viewbotter_walk_right_c1f7adc7.png',
    spriteSize: 48,
    // BETA 14.71 — BUG-2 from cycle-8: was 18; sprite/2 = 24. User intent
    // "if their bot sprite touched my bot its a hit". Hitbox now matches
    // visible sprite radius. Closes the 6 px gap.
    hitboxRadius: 24,

    // ── Base Stats ──
    baseHP: 38,                 // BETA 20.35 — Dylan (Megabonk feel): "most single-shot sprite weapons should be 1 shot." 50→38 so sniper(60)/revolver(50)/cannon(60)/fireball(51)/viking_axe(39) one-shot a Floor-1 zombie (global HP mult = 1.0). basketball + multi-shot/aura/bounce stay 2-shot. Elite variant (4×=152) + bosses keep their weight; per-floor HP mult is linear so F2 zombie = 76.
    baseDamage: 25,             // BETA 14.97 — user direction: zombie hit should be 25. Was 8 (BETA 14.73 cycle-9 dialed it down for bot survivability). Bot CSV (50-run, 1777696959810) shows 42-sec deaths to zombies even at dmg=8 + cooldown 0.5s — but per-hit damage was too low to register as "scary" in playtest. New value: 25/hit × 2/sec = 50 dps from one zombie at min 0.
    baseSpeed: 85,              // BETA 13.31: 100 → 85 (gives bot kiting room on floor 1)
    knockbackResist: 0.0,       // Full knockback
    contactKnockback: 320,      // BETA 21.70 — base shove force onto the player on contact (current global default)
    contactCooldown: 0.5,       // BETA 14.89: 0.7 → 0.5 per user direction (Megabonk-ish snappier feel). 2/sec max per zombie at touch range.
    iFrameGrant: 0,             // BETA 14.73 cycle-9: 0.2 → 0. BETA 14.71 made contact bypass iframe; now explicit zero.

    // ── Movement ──
    movement: 'chase',

    // ── Scaling ──
    scaling: {
      hpPerMinute: 0.7,        // BETA 16.20 — PSBOT cycle-23: 0.6 → 0.7. Slight bump for late-game zombie relevance (235 HP at min 6 → 265 HP). Keeps as fodder but prevents steamrolling at min 5+.
      damagePerMinute: 1.0,    // BETA 21.68 — now a RAMP MULT for enemyDamageScale (1 = safe anchored curve: ~60 dmg @5min = 2 hits on a fresh 100-HP player, ~105 @10min). Was 0.2 under the old linear per-minute model.
      speedPerMinute: 0.40,    // BETA 21.21 — 0.25 → 0.40 (Dylan fairness pass). Strong move-speed-stacked players were untouchable for 9 min (1 hit). The RAMP catches long-survivors (~206 speed @ min5, exceeds buffed kiters by min7-8) while barely touching the early game where average builds already die fairly at 3 min. Was lowered to 0.25 in 13.49 for BOT kiting — note bots will die a touch earlier now (PSBOT re-baseline).
    },

    // ── Elite Multipliers ──
    eliteMultipliers: {
      hp: 6.0,                 // BETA 21.48 — crowned zombies tankier (Dylan). 4→6.
      // BETA 20.01 — Dylan-approved nerf after Megabonk research: 2.0 → 1.5.
      // Elite zombie at 5min was dealing 25 × (1+0.2×5) × 1.2 × 2.0 = 120 dmg,
      // one-shotting 100 HP players. With 1.5×: 90 dmg = scary but survivable
      // with armor/shield/maxHp investment. Per Dylan: "the elites still hit
      // harder than normal but don't one-shot from full health." Matches
      // Megabonk philosophy (elites are tankier, not double-damaging).
      damage: 1.5,             // 1.5x damage (was 2.0)
      speed: 1.5,              // 1.5x speed
      size: 1.4,               // 40% bigger sprite
    },

    // ── Loot Drops ──
    drops: [
      { type: 'xp',    chance: 1.0,  amount: 35 },  // BETA 13.34: 20 → 35 (let bot level up; was finalLevel=1 every run)
      { type: 'goop',  chance: 1.0,  amount: 15 },  // BETA 15.54: 8 → 15 (REVERT 15.43 halve per Dylan: "put gold back to what it was as far as how much you were getting per kill. the double the cose in chest is pretty high. but works if we have the gold levels go back.")
      { type: 'health', chance: 0.05, amount: 5 },  // 5% chance for small heal
    ],
    eliteDrops: [
      { type: 'xp',    chance: 1.0,  amount: 50 },  // Big XP drop (was 25)
      { type: 'goop',  chance: 1.0,  amount: 25 },  // BETA 15.54: 12 → 25 (REVERT 15.43 halve)
      { type: 'chest', chance: 1.0,  amount: 1  },  // Guaranteed chest
    ],

    // ── Spawn Rules ──
    spawnWeight: 100,           // Most common enemy by far
    minSpawnTime: 0,            // Available from the very start
    maxActiveCount: 0,          // Unlimited

    tags: ['undead', 'basic', 'horde'],
  },

  // ─────────────────────────────────────────────────────────────
  //  23.55 — TIMMY IS FULLY DELETED on Dylan's order (real-streamer
  //  likeness, same treatment as CaseOh 23.39). His generic ability
  //  systems (throwAttack, intercept movement) remain in enemySystem
  //  for the Phase C original roster. Items that targeted him now
  //  target the 'brawler' TAG, so they wake up with the new roster.
  // ─────────────────────────────────────────────────────────────

  // 23.39 — the CaseOh boss (regular + mega final boss) is FULLY DELETED
  // on Dylan's order. New original bosses arrive with the Phase C enemy
  // roster redo.

  // ─────────────────────────────────────────────────────────────
  //  E1 (enemy rebuild) — the old parody-era placeholder roster comment was
  //  deleted. The real 12-enemy roster + specs live in
  //  docs/design/enemy-rebuild/PLAN.md §5 and land in Phases E2-E6.
  // ─────────────────────────────────────────────────────────────
};


// ═══════════════════════════════════════════════════════════════
//  HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/** Get an enemy definition by ID */
export function getEnemyDef(id: string): EnemyDefinition | undefined {
  return ENEMY_DATABASE[id];
}

/** Get all enemy IDs */
export function getAllEnemyIds(): string[] {
  return Object.keys(ENEMY_DATABASE);
}

/** Get all enemies available at a given time (seconds into run) */
export function getAvailableEnemies(runTimeSeconds: number): EnemyDefinition[] {
  return Object.values(ENEMY_DATABASE).filter(e => runTimeSeconds >= e.minSpawnTime);
}

// ─── Endless Mode (after the 10-minute timer hits 0:00) ──────────────
// BETA 15.45 — Per Dylan: "once you hit 0[:00] enemies should get god like.
// doubling health and damage and speed ever 20 seconds." After the 10-minute
// round timer expires, enemies enter endless escalation mode: hp/damage/speed
// all multiply by 2 every 20 seconds. By 11:00 enemies are 8× their 10:00
// stats; by 12:00 they're 64×; by 13:00 they're 512×. Designed to make
// long-survival runs end with a real cap, not unlimited farming.
//
// Speed clamp: hard-cap speed multiplier at 8× to prevent infinite-velocity
// frame-skip bugs — anything faster wraps the screen instantly anyway and
// breaks pathfinding. HP and damage have no cap so "god mode" still bites.
const ENDLESS_START_SECONDS = 600; // 10:00 timer reaches 0
const ENDLESS_DOUBLE_INTERVAL = 20; // seconds per doubling
const ENDLESS_SPEED_CAP_MULT = 10;  // BETA 21.24 — 8 → 10 (faster, still collidable)
const ENDLESS_AGGRO_PER_SEC = 0.6;  // BETA 21.24 — linear climb past the x32 cap

/** How much HP/damage/speed should be multiplied by endless mode at the given runtime. */
export function getEndlessMultiplier(runTimeSeconds: number): { hp: number; damage: number; speed: number } {
  if (runTimeSeconds < ENDLESS_START_SECONDS) {
    return { hp: 1, damage: 1, speed: 1 };
  }
  const elapsed = runTimeSeconds - ENDLESS_START_SECONDS;
  const doublings = elapsed / ENDLESS_DOUBLE_INTERVAL;
  // Phase 1: exponential doubling to x32 over the first ~100s (as before — the
  // x32 cap exists because the old uncapped pow(2,t) hit x6.69e+26 and CRASHED).
  const expMult = Math.min(Math.pow(2, doublings), 32);
  // BETA 21.24 — Phase 2: AGGRESSIVE LINEAR climb past x32 (Dylan: "after 10 min
  // scale aggressively, guaranteed death regardless of build"). LINEAR = can never
  // overflow. +0.6/sec past the cap → x32 @ 11:40, x92 @ 13:20, x152 @ 15:00,
  // x332 @ 20:00. No build survives. HP + damage climb; speed stays capped so
  // enemies are fast-but-collidable, not teleporting through walls.
  const aggroBonus = Math.max(0, elapsed - 100) * ENDLESS_AGGRO_PER_SEC;
  const mult = expMult + aggroBonus;
  return {
    hp: mult,
    damage: mult,
    speed: Math.min(expMult, ENDLESS_SPEED_CAP_MULT),
  };
}

// BETA 15.46 — Movement-only collision radius cap. Decoupled from
// hitboxRadius (which still drives damage detection and bullet hits). This
// is the radius used by isBlocked() in movement code paths only. 50r × 2
// = 100wu diameter, fits the narrowest doorway (E/W inner = 136wu) with
// 36wu margin (18wu per side). Per Dylan: applied to ALL enemies so big
// bosses (Timmy elite, etc.) walk through doorways without
// getting wedged, while their damage zone stays full-sprite-size.
export const PATHING_RADIUS_CAP = 50;

/**
 * Calculate scaled stats for an enemy at a given run time.
 * Returns the effective HP, damage, and speed after time-based scaling.
 */
// BETA 16.91 — PSBOT cycle-30i §3C + Dylan "yes": "fewer + harder
// enemies" rebalance. Spawn rate cut 50% (waveSystem SPAWN_GLOBAL_MULT
// below) paired with 2× HP and 1.55× damage on every enemy. Same DPS
// pressure with half the entities → fewer GC allocations, fewer
// per-frame physics checks, less screen clutter, but each enemy
// matters more. Mirror this 0.5/2.0/1.55 pair if Dylan ever asks for
// magnitude tuning.
// BETA 19.50 — Dylan live: "basketball should take 2 hits to kill a zombie
// for base damage. actually most weapons should." BETA 16.91 doubled zombie
// HP globally (1.0 → 2.0) which silently broke EVERY weapon's old "X-hit
// kill" tuning — comments throughout weapons.ts still reference "2-shot on
// 50-HP zombie" because they were balanced against the pre-16.91 HP curve.
// Rolling HP mult back to 1.0 restores those original 2-shot windows on
// normal zombies for: revolver (50 dmg), fireball wand (51), tactical rifle
// (51), shitty cannon (60), Karen drones (60), spatula L1+1lvl (~34), and
// most starter weapons. Elites + bosses still scale via their own
// multipliers (4× / 6×).
// DMG mult unchanged at 1.2 (the BETA 19.46 survivability fix stays).
const ENEMY_HP_GLOBAL_MULT = 1.0;
const ENEMY_DMG_GLOBAL_MULT = 1.2;

/**
 * BETA 21.68 — "Smart" anchored enemy-damage curve (Dylan's design rule). Instead of a
 * blind per-minute coefficient (where a fresh player got one-shot by min 5), damage
 * follows a DESIGNED curve relative to the enemy's base damage, with `aggro` as a ramp
 * multiplier (def.scaling.damagePerMinute; 1 = the safe baseline curve):
 *     scale(t) = 1 + aggro·(0.15·t + 0.01·t²)   (t in minutes)
 * At aggro 1: ×1.0 @0min, ×2.0 @5min, ×3.5 @10min, accelerating after. Tuned so the
 * baseline common (zombie, ~30 final base dmg) hits ~60 @5min (~2 hits on a fresh 100-HP
 * player, NO defense) and ~105 @10min (one-shot territory, when defense is expected).
 * Higher-base mini-bosses (timmy) are scarier by their base, by design. Raise aggro
 * for a steeper/deadlier ramp, lower it for gentler.
 */
export function enemyDamageScale(minutes: number, aggro: number): number {
  const t = Math.max(0, minutes);
  const a = Math.max(0, aggro);
  return 1 + a * (0.15 * t + 0.01 * t * t);
}

export function getScaledStats(
  def: EnemyDefinition,
  runTimeSeconds: number,
  variant: EnemyVariant = 'normal',
  floorMult: number = 1.0,  // BETA 19.98 — multiplies HP + damage by floor (1.0 = floor 1, 2.0 = floor 2, etc.) per Dylan's "2 times harder" spec. Was missing → floor 2 enemies were as weak as floor 1 minute 0 because state.time resets on floor advance.
  // BETA 20.77 — clock fix: endless overdrive runs on FLOOR-LOCAL seconds
  // (defaults to the monotonic clock for callers that don't pass it — lab
  // sims, replays — which matches single-floor behavior exactly).
  endlessSeconds: number = runTimeSeconds
): { hp: number; damage: number; speed: number; spriteSize: number; hitboxRadius: number; pathingRadius: number } {
  const minutes = runTimeSeconds / 60;

  // BETA 20.55 — Dylan: "enemies get too strong too fast — there should be a
  // SLOW BURN." The old ramp was LINEAR (a zombie gained +70% HP in the FIRST
  // minute), so a 1-shot weapon stopped 1-shotting ~25-30s in. Curve the TIME
  // so the ramp starts GENTLE and ACCELERATES instead of front-loading:
  //     rampMinutes = minutes² / (minutes + WARMUP)
  // Early (minutes ≪ WARMUP) it grows ~minutes²/WARMUP (slow — at 30s a zombie
  // is ~41 HP not 51, so single-shot weapons keep 1-shotting through the first
  // minute+); late it approaches the old linear rate shifted ~WARMUP min later,
  // so long runs still get hard, just on a gentler build-up. floorMult + the
  // 10-min endless doubling are UNCHANGED (per-floor steps + endless still bite
  // on schedule). Applied to HP, damage AND speed so enemies get stronger AND
  // faster on the same slow burn. Tune via WARMUP_MINUTES (bigger = slower).
  const WARMUP_MINUTES = 1.5;
  const rampMinutes = (minutes * minutes) / (minutes + WARMUP_MINUTES);

  let hp = def.baseHP * (1 + def.scaling.hpPerMinute * rampMinutes) * ENEMY_HP_GLOBAL_MULT * floorMult;
  // BETA 21.68 — DAMAGE uses the anchored "smart curve" (Dylan): designed so a fresh
  // 100-HP player (no defensive buffs) is NOT one-shot before ~7 min, and only reaches
  // one-shot territory around 10 min when defense is expected. `damagePerMinute` is now a
  // RAMP multiplier (1 = the safe designed curve; higher = steeper). See enemyDamageScale().
  let damage = def.baseDamage * enemyDamageScale(minutes, def.scaling.damagePerMinute) * ENEMY_DMG_GLOBAL_MULT * floorMult;
  let speed = def.baseSpeed * (1 + def.scaling.speedPerMinute * rampMinutes);
  let spriteSize = def.spriteSize;
  let hitboxRadius = def.hitboxRadius;

  if (variant === 'elite') {
    hp *= def.eliteMultipliers.hp;
    damage *= def.eliteMultipliers.damage;
    speed *= def.eliteMultipliers.speed;
    spriteSize *= def.eliteMultipliers.size;
    hitboxRadius *= def.eliteMultipliers.size;
  } else if (variant === 'boss' && def.bossMultipliers) {
    hp *= def.bossMultipliers.hp;
    damage *= def.bossMultipliers.damage;
    speed *= def.bossMultipliers.speed;
    spriteSize *= def.bossMultipliers.size;
    hitboxRadius *= def.bossMultipliers.size;
  }

  // BETA 15.45 — endless mode multiplier (kicks in at 10:00, doubles every 20s)
  // BETA 20.77 — floor-local seconds (see param doc above).
  const endless = getEndlessMultiplier(endlessSeconds);
  hp *= endless.hp;
  damage *= endless.damage;
  speed *= endless.speed;

  // BETA 15.46 — pathing radius is hitboxRadius capped at 50 so all enemies
  // (including elite/boss bosses) can move through 136wu-wide doorways.
  const pathingRadius = Math.min(hitboxRadius, PATHING_RADIUS_CAP);

  return {
    hp: Math.round(hp),
    damage: Math.round(damage),
    speed: Math.round(speed),
    spriteSize: Math.round(spriteSize),
    hitboxRadius: Math.round(hitboxRadius),
    pathingRadius: Math.round(pathingRadius),
  };
}

/**
 * Get the sprite URL for an enemy based on its movement direction.
 * For elite variants, returns the single elite sprite.
 */
export function getEnemySprite(
  def: EnemyDefinition,
  facing: SpriteDirection,
  variant: EnemyVariant = 'normal'
): string {
  if (variant === 'elite' || variant === 'boss') {
    return def.eliteSprite;
  }
  return def.sprites[facing];
}

/**
 * Roll loot drops for a killed enemy.
 * Returns an array of drops that succeeded their probability check.
 * luckMultiplier increases drop chances (from player Luck stat).
 */
export function rollEnemyDrops(
  def: EnemyDefinition,
  variant: EnemyVariant = 'normal',
  luckMultiplier: number = 1.0
): EnemyLootDrop[] {
  // Boss gets drops + eliteDrops and 3x all amounts
  const dropTable = variant === 'boss'
    ? [...def.drops, ...def.eliteDrops].map(d => ({ ...d, amount: d.amount * 3 }))
    : variant === 'elite'
      ? [...def.drops, ...def.eliteDrops]
      : def.drops;

  const results: EnemyLootDrop[] = [];

  for (const drop of dropTable) {
    const effectiveChance = Math.min(drop.chance * luckMultiplier, 1.0);
    if (Math.random() < effectiveChance) {
      results.push({ ...drop });
    }
  }

  return results;
}
